/* import __COLOCATED_TEMPLATE__ from './teams.hbs'; */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import ENV from 'bullitttime-event/config/environment';

// Base fields for mapping dropdown (team info fields)
const BASE_FIELDS = [
	{ value: '', label: '-- Not mapped --' },
	{ value: 'name', label: 'Team Name', required: true },
	{ value: 'bib', label: 'Team Bib' },
	{ value: 'club', label: 'Club' },
	{ value: 'country', label: 'Country' },
	{ value: 'id1', label: 'ID 1' },
	{ value: 'id2', label: 'ID 2' },
	{ value: 'id3', label: 'ID 3' }
];

// Fields that can have custom labels from the event
const CUSTOMIZABLE_FIELDS = ['id1', 'id2', 'id3'];

export default class TeamsUploadComponent extends Component
{
	@service store;
	@service errorManager;

	// Step tracking: 'select', 'mapping', 'results'
	@tracked currentStep = 'select';

	// Upload options
	@tracked shouldUpdate = true;
	@tracked shouldInsert = true;
	@tracked shouldDrop = false;

	// Analysis results
	@tracked sessionId = null;
	@tracked headers = [];
	@tracked mapping = {};
	@tracked memberColumns = [];
	@tracked unmappedColumns = [];
	@tracked previewRows = [];
	@tracked totalRows = 0;
	@tracked requiredFields = [];
	@tracked missingRequired = [];
	@tracked customFieldLabels = {};

	// Processing state
	@tracked isAnalyzing = false;
	@tracked isConfirming = false;

	// Final results
	@tracked updated = "";
	@tracked inserted = "";
	@tracked dropped = "";
	@tracked errors = [];

	// Build available fields dynamically using custom labels from event
	get availableFields() {
		const fields = [...BASE_FIELDS];

		// Update customizable fields with their custom labels (if set)
		for (const field of fields) {
			if (CUSTOMIZABLE_FIELDS.includes(field.value)) {
				const customLabel = this.customFieldLabels[field.value];
				if (customLabel && customLabel !== `ID ${field.value.slice(-1)}`) {
					field.label = customLabel;
				}
			}
		}

		return fields;
	}

	get baseUrl() {
		return ENV.eventBackendProtocol + "://" + ENV.eventBackendHost + ":" + ENV.eventBackendPort + (ENV.eventBackendNamespace ? "/" + ENV.eventBackendNamespace : "");
	}

	get columnMappingList() {
		// Create an array of column objects for the template
		return this.headers.map((header, index) => {
			// Find if this column is mapped to a field
			let mappedField = '';
			for (const [field, fieldMapping] of Object.entries(this.mapping)) {
				if (fieldMapping.columnIndex === index) {
					mappedField = field;
					break;
				}
			}

			// Check if this column is a member column
			const isMemberColumn = this.memberColumns.some(mc => {
				const colIndex = typeof mc === 'number' ? mc : mc.columnIndex;
				return colIndex === index;
			});

			return {
				index,
				header,
				mappedField,
				isMemberColumn,
				isRequired: this.requiredFields.includes(mappedField),
				isMissing: !mappedField && !isMemberColumn && this.unmappedColumns.some(u => u.columnIndex === index)
			};
		});
	}

	get hasMissingRequired() {
		return this.missingRequired.length > 0;
	}

	get canConfirm() {
		return !this.hasMissingRequired && !this.isConfirming;
	}

	get memberColumnCount() {
		return this.memberColumns.length;
	}

	@action
	toggleShouldInsert(event)
	{
		this.shouldInsert = event.target.checked;
	}

	@action
	toggleShouldUpdate(event)
	{
		this.shouldUpdate = event.target.checked;
	}

	@action
	toggleShouldDrop(event)
	{
		this.shouldDrop = event.target.checked;
	}

	@action
	async analyzeFile(file)
	{
		this.isAnalyzing = true;

		try {
			const url = this.baseUrl + "/events/" + this.args.eventId + "/analyzeTeams";
			const fetchResponse = await file.upload(url);

			// Read the full response body (handles chunked responses properly)
			const responseText = await fetchResponse.text();
			const response = JSON.parse(responseText);

			if (response.error) {
				this.errorManager.addError(response.error, { clearable: true, type: 'validation' });
				this.isAnalyzing = false;
				return;
			}

			// Store analysis results
			this.sessionId = response.sessionId;
			this.headers = response.headers;
			this.mapping = response.mapping;
			this.memberColumns = response.memberColumns || [];
			this.unmappedColumns = response.unmappedColumns || [];
			this.previewRows = response.preview?.sampleRows || [];
			this.totalRows = response.preview?.totalRows || 0;
			this.requiredFields = response.requiredFields || [];
			this.missingRequired = response.missingRequired || [];
			this.customFieldLabels = response.customFieldLabels || {};

			// Move to mapping step
			this.currentStep = 'mapping';
		} catch (error) {
			this.errorManager.addError('Failed to analyze file: ' + error.message, { clearable: true, type: 'network' });
		}

		this.isAnalyzing = false;
	}

	@action
	updateColumnMapping(columnIndex, event) {
		const newField = event.target.value;
		const oldMapping = { ...this.mapping };

		// Remove any existing mapping for this column index
		for (const [field, fieldMapping] of Object.entries(oldMapping)) {
			if (fieldMapping.columnIndex === columnIndex) {
				delete oldMapping[field];
				break;
			}
		}

		// Remove from member columns if it was there
		this.memberColumns = this.memberColumns.filter(mc => {
			const colIndex = typeof mc === 'number' ? mc : mc.columnIndex;
			return colIndex !== columnIndex;
		});

		// If selecting a new field, remove any existing mapping for that field
		// and add the new mapping
		if (newField) {
			delete oldMapping[newField];
			oldMapping[newField] = {
				columnIndex,
				confidence: 'user',
				headerName: this.headers[columnIndex]
			};
		}

		this.mapping = oldMapping;

		// Recalculate missing required
		this.missingRequired = this.requiredFields.filter(field => !this.mapping[field]);
	}

	@action
	toggleMemberColumn(columnIndex) {
		const isCurrentlyMember = this.memberColumns.some(mc => {
			const colIndex = typeof mc === 'number' ? mc : mc.columnIndex;
			return colIndex === columnIndex;
		});

		if (isCurrentlyMember) {
			// Remove from member columns
			this.memberColumns = this.memberColumns.filter(mc => {
				const colIndex = typeof mc === 'number' ? mc : mc.columnIndex;
				return colIndex !== columnIndex;
			});
		} else {
			// Remove from regular mapping first
			const oldMapping = { ...this.mapping };
			for (const [field, fieldMapping] of Object.entries(oldMapping)) {
				if (fieldMapping.columnIndex === columnIndex) {
					delete oldMapping[field];
					break;
				}
			}
			this.mapping = oldMapping;

			// Add to member columns
			this.memberColumns = [...this.memberColumns, {
				columnIndex,
				headerName: this.headers[columnIndex]
			}];
		}

		// Force reactivity update
		this.memberColumns = [...this.memberColumns];
	}

	@action
	async confirmUpload() {
		this.isConfirming = true;

		try {
			const url = this.baseUrl + "/events/" + this.args.eventId + "/confirmTeams";

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({
					sessionId: this.sessionId,
					mapping: this.mapping,
					memberColumns: this.memberColumns,
					options: {
						insert: this.shouldInsert,
						update: this.shouldUpdate,
						drop: this.shouldDrop
					}
				})
			});

			const result = await response.json();

			if (!response.ok) {
				this.errorManager.addError(result.error || 'Failed to process teams', { clearable: true, type: 'validation' });
				this.isConfirming = false;
				return;
			}

			// Store results
			this.inserted = result.numRecordsInserted;
			this.updated = result.numRecordsUpdated;
			this.dropped = result.numRecordsDropped;
			this.errors = result.errors || [];

			// Refresh store data
			const store = this.store;

			var teams = store.peekAll('Team');
			teams.forEach((modelInstance) => modelInstance.unloadRecord());

			var entries = store.peekAll('Entry');
			entries.forEach((modelInstance) => modelInstance.unloadRecord());

			var events = store.peekAll('Event');
			events.forEach((modelInstance) =>
			{
				store.findRecord('Event', modelInstance.id, { reload: true });
			});

			// Move to results step
			this.currentStep = 'results';
		} catch (error) {
			this.errorManager.addError('Failed to process teams: ' + error.message, { clearable: true, type: 'network' });
		}

		this.isConfirming = false;
	}

	@action
	startOver() {
		// Reset to initial state
		this.currentStep = 'select';
		this.sessionId = null;
		this.headers = [];
		this.mapping = {};
		this.memberColumns = [];
		this.unmappedColumns = [];
		this.previewRows = [];
		this.totalRows = 0;
		this.missingRequired = [];
		this.customFieldLabels = {};
		this.inserted = "";
		this.updated = "";
		this.dropped = "";
		this.errors = [];
	}

	@action
	goBack() {
		if (this.currentStep === 'mapping') {
			this.startOver();
		} else if (this.currentStep === 'results') {
			this.currentStep = 'mapping';
		}
	}
}
